import type { ConfirmModalOptions, InfoModalOptions } from "@/services/simple-modal/simple-modal-types";
import { useStore } from "@/store/store";

import { registerResolver, resolveConfirm, resolveInfo } from "./resolver-registry";

export type { ConfirmModalOptions, InfoModalOptions };

/**
 * Closes any currently open modal before a new one is displayed. Evicted promise-based
 * modals resolve as `false` by default.
 */
const settleExistingSimpleModal = (): void => {
    const { simpleModalDescriptor, closeSimpleModal } = useStore.getState();
    if (!simpleModalDescriptor) {
        return;
    }
    if (simpleModalDescriptor.type === "confirm") {
        resolveConfirm(simpleModalDescriptor.requestId, false);
    } else {
        resolveInfo(simpleModalDescriptor.requestId);
    }
    closeSimpleModal();
};

/** Programmatically close and settle the currently open modal. */
export const closeModal = (): void => {
    settleExistingSimpleModal();
};

/**
 * Open a confirm modal.
 */
export const confirmModal = (options: ConfirmModalOptions): Promise<boolean> => {
    settleExistingSimpleModal();
    const requestId = crypto.randomUUID();
    return new Promise<boolean>((resolve, reject) => {
        registerResolver(requestId, { type: "confirm", resolve, reject });
        useStore.getState().openSimpleModal({
            type: "confirm",
            requestId,
            title: options.title,
            body: options.body,
            confirmLabel: options.confirmLabel ?? "Confirm",
            cancelLabel: options.cancelLabel ?? "Cancel",
            tone: options.tone ?? "default",
            // Danger modals require an explicit action by default — they are non-dismissible unless overridden.
            dismissible: options.dismissible ?? options.tone !== "danger",
            onConfirm: options.onConfirm,
            onDismiss: options.onDismiss,
        });
    });
};

/**
 * Open an info modal.
 */
export const infoModal = (options: InfoModalOptions): Promise<void> => {
    settleExistingSimpleModal();
    const requestId = crypto.randomUUID();
    return new Promise<void>((resolve) => {
        registerResolver(requestId, { type: "info", resolve });
        useStore.getState().openSimpleModal({
            type: "info",
            requestId,
            title: options.title,
            body: options.body,
            closeLabel: options.closeLabel ?? "OK",
            tone: options.tone ?? "info",
            dismissible: options.dismissible ?? true,
        });
    });
};
